// src/pages/monitoring/MonitoringDashboard.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  AlertTriangle, Clock, CheckCircle, TrendingUp,
  Download, BarChart3, Loader2, Bell, Shield,
  Eye, ChevronRight, ChevronLeft, Hourglass,
  AlertCircle, PlayCircle, Users, ArrowUpRight,
  Calendar, Activity, Sparkles,
} from 'lucide-react';
import { Button, Badge, Card, Breadcrumb, ProgressBar, Select, Loading } from '../../components/ui';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar,
} from 'recharts';
import { monitoringService, DashboardKPI, OverdueItem, ReminderData, RiwayatPengingatItem } from '../../services/monitoring';
import { PpeppRekomendasi, Periode, TingkatRisiko } from '../../types';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import { getStatusLabel, STATUS_CONFIG } from '../../utils/constants';
import toast from 'react-hot-toast';

// ========== CONSTANTS ==========
const PPEPP_PER_PAGE = 5;
const MAX_UNITS_DISPLAY = 8; // Maksimal unit yang ditampilkan di chart

// Tab configuration untuk PPEPP
const PPEPP_TABS = [
  { id: 'semua', label: 'Semua', icon: null, color: 'text-neutral-text-primary' },
  { id: 'berlangsung', label: 'Berlangsung', icon: PlayCircle, color: 'text-warning' },
  { id: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi', icon: Hourglass, color: 'text-amber-600' },
  { id: 'terhambat', label: 'Terhambat', icon: AlertCircle, color: 'text-danger' },
  { id: 'selesai_final', label: 'Selesai', icon: CheckCircle, color: 'text-success' },
] as const;

type PpeppTabId = typeof PPEPP_TABS[number]['id'];

// ========== CUSTOM COMPONENTS ==========

// KPI Card dengan gradient dan animasi
interface KpiCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconGradient?: string;
  label: string;
  value: number | string;
  valueColor?: string;
  subtitle?: string;
  pulse?: boolean;
  trend?: 'up' | 'down' | 'neutral';
}

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  iconBg,
  iconGradient,
  label,
  value,
  valueColor = 'text-neutral-text-primary',
  subtitle,
  pulse = false,
  trend,
}) => (
  <div className="relative group bg-white rounded-2xl border border-neutral-border/50 p-5 shadow-sm hover:shadow-lg hover:border-neutral-border transition-all duration-300 overflow-hidden">
    {/* Subtle gradient background on hover */}
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${iconGradient || 'bg-gradient-to-br from-teal-50/50 to-transparent'}`} />
    
    {/* Pulse indicator */}
    {pulse && (
      <div className="absolute top-3 right-3">
        <span className="flex h-2.5 w-2.5 relative">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger"></span>
        </span>
      </div>
    )}
    
    <div className="relative z-10 flex items-start gap-4">
      <div className={`p-3 rounded-xl ${iconBg} shadow-sm`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-1">
          {label}
        </p>
        <p className={`text-3xl font-black tracking-tight ${valueColor}`}>
          {value}
        </p>
        {subtitle && (
          <p className="text-[11px] text-neutral-text-secondary mt-0.5 flex items-center gap-1">
            {trend === 'up' && <ArrowUpRight className="w-3 h-3 text-success" />}
            {trend === 'down' && <ArrowUpRight className="w-3 h-3 text-danger rotate-90" />}
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </div>
);

// Section Header Component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-xl bg-gradient-to-br from-teal-main/10 to-teal-main/5">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-bold text-teal-dark">{title}</h3>
        {subtitle && <p className="text-xs text-neutral-text-secondary">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

// ========== MAIN COMPONENT ==========
const MonitoringDashboard: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<DashboardKPI | null>(null);
  const [overdueList, setOverdueList] = useState<OverdueItem[]>([]);
  const [ppeppList, setPpeppList] = useState<PpeppRekomendasi[]>([]);
  const [unitPerformance, setUnitPerformance] = useState<any[]>([]);
  const [reminders, setReminders] = useState<ReminderData[]>([]);
  const [reminderHistory, setReminderHistory] = useState<RiwayatPengingatItem[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [kpis, setKpis] = useState<any[]>([]);
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);

  // PPEPP Tab & Pagination State
  const [ppeppActiveTab, setPpeppActiveTab] = useState<PpeppTabId>('semua');
  const [ppeppCurrentPage, setPpeppCurrentPage] = useState(1);

  // Unit Performance - Show All toggle
  const [showAllUnits, setShowAllUnits] = useState(false);

  const role = useRole();
  const { user } = useAuthStore();

  // ========== ACCESS CONTROL ==========
  const blockedRoles = ['kaprodi', 'kabag', 'ka_tu'];
  const userRoleName = user?.role?.name?.toLowerCase() || '';
  const isBlockedRole = blockedRoles.includes(userRoleName);

  const canAccessMonitoringDashboard =
    !isBlockedRole && (
      role.isAdmin ||
      role.isRektor ||
      role.isWarek ||
      role.isDekan ||
      role.isKalem ||
      role.isObserver
    );

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (!canAccessMonitoringDashboard) return;
    if (!isPeriodeReady) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const filters = periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : undefined;

        const results = await Promise.allSettled([
          monitoringService.getDashboard(filters),
          monitoringService.getOverdue(filters ? { ...filters, per_page: 5 } : { per_page: 5 }),
          monitoringService.getProgressPpepp(filters ? { ...filters, per_page: 100 } : { per_page: 100 }),
          monitoringService.getUnitPerformance(filters),
          monitoringService.getReminders(),
          monitoringService.getKpiDashboard(filters),
          role.canManageRapim
            ? monitoringService.getReminderHistory({ per_page: 5 })
            : Promise.resolve({ data: [] }),
        ]);

        const [kpiRes, overdueRes, ppeppRes, unitRes, reminderRes, kpiListRes, reminderHistoryRes] = results;

        const isNetworkError = (reason: any) => !reason?.response;
        const coreFailures = [kpiRes, overdueRes, ppeppRes, unitRes].filter(r => r.status === 'rejected');
        if (coreFailures.length === 4 && coreFailures.every(r => isNetworkError((r as PromiseRejectedResult).reason))) {
          toast.error('Backend tidak dapat diakses. Pastikan server berjalan dan URL API sudah benar.');
        } else if (results.some(r => r.status === 'rejected')) {
          toast.error('Sebagian data monitoring gagal dimuat.');
        }

        if (kpiRes.status === 'fulfilled') {
          setKpi((kpiRes.value as any)?.data || kpiRes.value);
        }

        if (overdueRes.status === 'fulfilled') {
          const overdueData = (overdueRes.value as any)?.data || overdueRes.value;
          setOverdueList(Array.isArray(overdueData) ? overdueData : (overdueData?.data || []));
        } else {
          setOverdueList([]);
        }

        if (ppeppRes.status === 'fulfilled') {
          const ppeppData = (ppeppRes.value as any)?.data || ppeppRes.value;
          setPpeppList(Array.isArray(ppeppData) ? ppeppData : (ppeppData?.data || []));
        } else {
          setPpeppList([]);
        }

        if (unitRes.status === 'fulfilled') {
          const unitData = (unitRes.value as any)?.data || unitRes.value;
          setUnitPerformance(Array.isArray(unitData) ? unitData : (unitData?.data || []));
        } else {
          setUnitPerformance([]);
        }

        if (reminderRes.status === 'fulfilled') {
          const remData = (reminderRes.value as any)?.data || reminderRes.value;
          setReminders(Array.isArray(remData) ? remData : []);
        } else {
          setReminders([]);
        }

        if (kpiListRes.status === 'fulfilled') {
          const kpiListData = (kpiListRes.value as any)?.data || kpiListRes.value;
          setKpis(Array.isArray(kpiListData) ? kpiListData : (kpiListData?.data || []));
        } else {
          setKpis([]);
        }

        if (reminderHistoryRes.status === 'fulfilled') {
          const historyData = (reminderHistoryRes.value as any)?.data || reminderHistoryRes.value;
          setReminderHistory(Array.isArray(historyData) ? historyData : (historyData?.data || []));
        } else {
          setReminderHistory([]);
        }
      } catch (e) {
        console.error(e);
        toast.error('Gagal memuat data monitoring');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [periodFilter, role.canManageRapim, canAccessMonitoringDashboard, isPeriodeReady]);

  useEffect(() => {
    if (!canAccessMonitoringDashboard) return;

    const fetchPeriode = async () => {
      try {
        const data = await monitoringService.getPeriodeList();
        const normalizedPeriode = Array.isArray(data) ? data : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p) => p.is_active);
        setPeriodFilter(activePeriode ? String(activePeriode.id) : 'all');
      } catch (e) {
        console.error('Gagal memuat daftar periode:', e);
        toast.error('Gagal memuat daftar periode');
      } finally {
        setIsPeriodeReady(true);
      }
    };
    fetchPeriode();
  }, [canAccessMonitoringDashboard]);

  // Reset pagination when tab changes
  useEffect(() => {
    setPpeppCurrentPage(1);
  }, [ppeppActiveTab]);

  // ========== HANDLERS ==========
  const handleExport = async () => {
    try {
      const arrayBuffer = await monitoringService.exportReport('pdf', periodFilter !== 'all' ? { periode_id: Number(periodFilter) } : undefined);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monitoring-report-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export Report Error:', e);
      toast.error('Gagal mengexport laporan');
    }
  };

  const handleSendReminder = async (laporanId: number) => {
    try {
      const result = await monitoringService.sendReminder(laporanId);
      toast.success((result as any)?.message || 'Pengingat berhasil dikirim');

      if (role.canManageRapim) {
        const historyRes = await monitoringService.getReminderHistory({ per_page: 5 }).catch(() => ({ data: [] }));
        const historyData = (historyRes as any)?.data || historyRes;
        setReminderHistory(Array.isArray(historyData) ? historyData : (historyData?.data || []));
      }
    } catch (e) {
      toast.error('Gagal mengirim pengingat');
    }
  };

  const normalizeOverdueDays = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.round(Math.abs(parsed)));
  };

  const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
    const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
    const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
    return validRisks.includes(normalized) ? normalized : 'sedang';
  };

  // ========== PPEPP FILTERING & PAGINATION ==========
  const ppeppStatusCounts = useMemo(() => {
    const counts: Record<PpeppTabId, number> = {
      semua: ppeppList.length,
      berlangsung: 0,
      menunggu_konfirmasi: 0,
      terhambat: 0,
      selesai_final: 0,
    };

    ppeppList.forEach((item) => {
      const kondisi = (item as any).kondisi_selesai?.toLowerCase() || '';
      if (kondisi === 'berlangsung') counts.berlangsung++;
      else if (kondisi === 'menunggu_konfirmasi') counts.menunggu_konfirmasi++;
      else if (kondisi === 'terhambat') counts.terhambat++;
      else if (kondisi === 'selesai_final') counts.selesai_final++;
    });

    return counts;
  }, [ppeppList]);

  const filteredPpeppList = useMemo(() => {
    if (ppeppActiveTab === 'semua') return ppeppList;
    return ppeppList.filter((item) => {
      const kondisi = (item as any).kondisi_selesai?.toLowerCase() || '';
      return kondisi === ppeppActiveTab;
    });
  }, [ppeppList, ppeppActiveTab]);

  const ppeppTotalPages = Math.ceil(filteredPpeppList.length / PPEPP_PER_PAGE);
  const paginatedPpeppList = useMemo(() => {
    const startIndex = (ppeppCurrentPage - 1) * PPEPP_PER_PAGE;
    return filteredPpeppList.slice(startIndex, startIndex + PPEPP_PER_PAGE);
  }, [filteredPpeppList, ppeppCurrentPage]);

  // ========== COMPUTED VALUES ==========
  const statusPieData = Object.entries(kpi?.laporan_per_status || {})
    .filter(([key]) => !['tugas_ppepp_aktif', 'draf'].includes(key))
    .map(([key, value]) => ({
      name: getStatusLabel(key),
      value,
      color: STATUS_CONFIG[key]?.color || '#9CA3AF',
    }))
    .filter(d => d.value > 0);

  const inProgressStatuses = ['draf', 'diajukan', 'validasi_l1', 'validasi_l2', 'antrean_rapim', 'diputuskan', 'berlangsung'];
  const inProgressCount = Object.entries(kpi?.laporan_per_status || {})
    .filter(([k]) => inProgressStatuses.includes(k))
    .reduce((acc, [, v]) => acc + v, 0);

  const totalSelesaiFinal =
    (kpi?.laporan_per_status?.['selesai'] || 0) +
    ((kpi?.laporan_per_status as any)?.['diarsipkan'] || 0);

  const periodeOptions = [
    { value: 'all', label: 'Semua Periode' },
    ...periodeList.map((periode) => ({
      value: String(periode.id),
      label: `${periode.nama}${periode.is_active ? ' (Aktif)' : ''}`,
    })),
  ];

  // Unit Performance - sorted & limited
  const sortedUnitPerformance = useMemo(() => {
    return [...unitPerformance]
      .map(u => ({
        ...u,
        completion_rate: u.total > 0 ? Math.round((u.selesai / u.total) * 100) : 0,
      }))
      .sort((a, b) => b.completion_rate - a.completion_rate);
  }, [unitPerformance]);

  const displayedUnits = showAllUnits 
    ? sortedUnitPerformance 
    : sortedUnitPerformance.slice(0, MAX_UNITS_DISPLAY);

  // ========== REDIRECT JIKA TIDAK PUNYA AKSES ==========
  if (!canAccessMonitoringDashboard) {
    return <Navigate to="/dashboard" replace />;
  }

  // ========== LOADING STATE ==========
  if (loading) {
        return <Loading variant="page" message="Memuat data..." />;
    }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Monitoring' },
      ]} />

      {/* Header - Clean Style without background */}
      <div className="relative mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-teal-main" />
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-main">Real-time</span>
            </div>
            <h1 className="text-2xl font-black tracking-tight text-teal-dark">Dashboard Monitoring</h1>
            <p className="text-sm text-neutral-text-secondary mt-1">Pantau progress dan tindak lanjut secara real-time</p>
          </div>
          <div className="flex items-center gap-2">
            <Select
              options={periodeOptions}
              value={periodFilter}
              onChange={setPeriodFilter}
              className="min-w-[180px]"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExport} 
              icon={<Download className="w-4 h-4" />}
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Summary - Bento Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<AlertTriangle className="w-6 h-6 text-danger" />}
          iconBg="bg-danger/10"
          iconGradient="bg-gradient-to-br from-red-50 to-transparent"
          label="Keterlambatan"
          value={kpi?.laporan_overdue || 0}
          valueColor="text-danger"
          subtitle="Perlu penanganan segera"
          pulse={(kpi?.laporan_overdue || 0) > 0}
        />
        <KpiCard
          icon={<Clock className="w-6 h-6 text-warning" />}
          iconBg="bg-warning/10"
          iconGradient="bg-gradient-to-br from-amber-50 to-transparent"
          label="Dalam Proses"
          value={inProgressCount}
          valueColor="text-warning"
          subtitle={`${ppeppStatusCounts.berlangsung} aktif dikerjakan`}
        />
        <KpiCard
          icon={<CheckCircle className="w-6 h-6 text-success" />}
          iconBg="bg-success/10"
          iconGradient="bg-gradient-to-br from-emerald-50 to-transparent"
          label="Selesai"
          value={totalSelesaiFinal}
          valueColor="text-success"
          subtitle="Total selesai & arsip"
          trend="up"
        />
        <KpiCard
          icon={<TrendingUp className="w-6 h-6 text-teal-main" />}
          iconBg="bg-teal-main/10"
          iconGradient="bg-gradient-to-br from-teal-50 to-transparent"
          label="Progress PPEPP"
          value={`${Math.round(kpi?.progress_ppepp || 0)}%`}
          valueColor="text-teal-main"
          subtitle="Tingkat penyelesaian"
        />
      </div>

      {/* Main Grid - Charts & Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Status Distribution - Donut Chart */}
        <Card className="lg:col-span-4" padding="lg">
          <SectionHeader
            icon={<Shield className="w-5 h-5 text-teal-main" />}
            title="Distribusi Status"
            subtitle={`${statusPieData.reduce((a, b) => a + b.value, 0)} total laporan`}
          />
          
          {statusPieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie 
                    data={statusPieData} 
                    cx="50%" 
                    cy="50%" 
                    innerRadius={55} 
                    outerRadius={85}
                    paddingAngle={3} 
                    dataKey="value" 
                    nameKey="name"
                    stroke="none"
                  >
                    {statusPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(v) => `${v} laporan`}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      fontSize: '12px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 w-full mt-2">
                {statusPieData.slice(0, 6).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-neutral-bg/50">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-[10px] text-neutral-text-secondary truncate flex-1">{item.name}</span>
                    <span className="text-xs font-bold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <p className="text-sm text-neutral-text-secondary">Belum ada data status.</p>
            </div>
          )}
        </Card>

        {/* Overdue Table - Premium Design */}
        <Card className="lg:col-span-8" padding="lg">
          <SectionHeader
            icon={<AlertTriangle className="w-5 h-5 text-danger" />}
            title="Laporan Keterlambatan"
            subtitle={`${overdueList.length} laporan melewati deadline`}
            action={
              <Link 
                to="/laporan?status=overdue" 
                className="text-xs font-semibold text-teal-main hover:text-teal-dark flex items-center gap-1 transition-colors"
              >
                Lihat Semua <ChevronRight className="w-3 h-3" />
              </Link>
            }
          />
          
          <div className="space-y-2">
            {overdueList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mb-3">
                  <CheckCircle className="w-7 h-7 text-success" />
                </div>
                <p className="text-sm font-semibold text-success">Tidak ada keterlambatan!</p>
                <p className="text-xs text-neutral-text-secondary mt-1">Semua laporan dalam target waktu</p>
              </div>
            ) : (
              overdueList.map((item, i) => {
                const targetEfektif = item.target_efektif || item.laporan?.target_selesai;
                const sumberTarget = item.sumber_target === 'keputusan_rapim' ? 'RAPIM' : 'SLA';
                const days = normalizeOverdueDays(item.hari_terlambat);
                
                return (
                  <Link
                    key={i}
                    to={`/laporan/${item.laporan?.id}`}
                    className="group flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-danger/5 to-transparent border border-danger/10 hover:border-danger/30 hover:shadow-md transition-all duration-300"
                  >
                    {/* Days Badge */}
                    <div className="shrink-0 w-16 text-center">
                      <div className="inline-flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-danger/10 group-hover:bg-danger/20 transition-colors">
                        <p className="text-xl font-black text-danger leading-none">{days}</p>
                        <p className="text-[9px] font-bold text-danger uppercase tracking-wider">Hari</p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                          {item.laporan?.kode}
                        </span>
                        <Badge variant="risk" risk={toRiskLevel(item.laporan?.tingkat_risiko)} />
                        <span className="text-[10px] text-neutral-text-secondary">• {sumberTarget}</span>
                      </div>
                      <p className="text-sm font-semibold text-neutral-text-primary truncate group-hover:text-danger transition-colors">
                        {item.laporan?.judul}
                      </p>
                      <p className="text-xs text-neutral-text-secondary mt-0.5">
                        {item.laporan?.unit?.name} • PIC: {item.pic}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {role.canManageRapim && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleSendReminder(item.laporan.id);
                          }}
                          className="p-2 rounded-lg bg-warning/10 hover:bg-warning/20 text-warning transition-colors"
                          title="Kirim Pengingat"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                      )}
                      <div className="p-2 rounded-lg bg-neutral-bg group-hover:bg-teal-main/10 transition-colors">
                        <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary group-hover:text-teal-main" />
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* Second Row: Unit Performance & Reminders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Unit Performance - Horizontal Bar Chart with Scroll */}
        <Card padding="lg">
          <SectionHeader
            icon={<Users className="w-5 h-5 text-teal-main" />}
            title="Kinerja per Unit"
            subtitle={`${sortedUnitPerformance.length} unit terpantau`}
            action={
              sortedUnitPerformance.length > MAX_UNITS_DISPLAY && (
                <button
                  onClick={() => setShowAllUnits(!showAllUnits)}
                  className="text-xs font-semibold text-teal-main hover:text-teal-dark transition-colors"
                >
                  {showAllUnits ? 'Tampilkan Sedikit' : `Lihat Semua (${sortedUnitPerformance.length})`}
                </button>
              )
            }
          />
          
          {sortedUnitPerformance.length > 0 ? (
            <div className={`space-y-3 ${showAllUnits ? 'max-h-[400px] overflow-y-auto pr-2 scrollbar-thin' : ''}`}>
              {displayedUnits.map((unit, i) => {
                const rate = unit.completion_rate || 0;
                const colorClass = rate >= 80 ? 'bg-success' : rate >= 50 ? 'bg-warning' : 'bg-danger';
                const bgClass = rate >= 80 ? 'bg-success/10' : rate >= 50 ? 'bg-warning/10' : 'bg-danger/10';
                
                return (
                  <div 
                    key={i} 
                    className={`group p-3 rounded-xl border border-neutral-border/50 hover:border-neutral-border hover:shadow-sm transition-all ${bgClass}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-bold text-neutral-text-secondary w-5">{i + 1}</span>
                        <span className="text-sm font-semibold text-neutral-text-primary truncate">
                          {unit.unit}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="text-success font-bold">{unit.selesai}</span>
                          <span className="text-neutral-text-secondary">/</span>
                          <span className="text-neutral-text-secondary">{unit.total}</span>
                        </div>
                        <span className={`text-sm font-black ${
                          rate >= 80 ? 'text-success' : rate >= 50 ? 'text-warning' : 'text-danger'
                        }`}>
                          {rate}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-neutral-border/30 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
                        style={{ width: `${rate}%` }}
                      />
                    </div>
                    {unit.overdue > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-danger">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{unit.overdue} terlambat</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-sm text-neutral-text-secondary">Belum ada data kinerja unit.</p>
            </div>
          )}
        </Card>

        {/* Reminders - Timeline Style */}
        <Card padding="lg">
          <SectionHeader
            icon={<Bell className="w-5 h-5 text-warning" />}
            title="Pengingat Tenggat Waktu"
            subtitle="H-7 sampai H-1"
          />
          
          {reminders.length > 0 ? (
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-warning via-orange-400 to-danger" />
              
              <div className="space-y-3">
                {reminders.slice(0, 5).map((r, i) => {
                  const targetEfektif = r.target_efektif || r.target_selesai;
                  const isOverdue = r.tipe === 'overdue';
                  const isUrgent = r.tipe === 'h-1';
                  const isWarning = r.tipe === 'h-3';
                  
                  const bgColor = isOverdue ? 'from-danger/10 to-transparent border-danger/20' :
                                  isUrgent ? 'from-red-50 to-transparent border-red-200' :
                                  isWarning ? 'from-orange-50 to-transparent border-orange-200' :
                                  'from-yellow-50 to-transparent border-yellow-200';
                  
                  const dotColor = isOverdue ? 'bg-danger' :
                                   isUrgent ? 'bg-red-500' :
                                   isWarning ? 'bg-orange-500' : 'bg-yellow-500';
                  
                  return (
                    <Link
                      key={i}
                      to={`/laporan/${r.laporan_id}`}
                      className={`group relative flex items-center gap-4 pl-10 pr-4 py-3 rounded-xl bg-gradient-to-r ${bgColor} border hover:shadow-md transition-all`}
                    >
                      {/* Timeline Dot */}
                      <div className={`absolute left-3.5 w-3 h-3 rounded-full ${dotColor} ring-4 ring-white`} />
                      
                      {/* Days Indicator */}
                      <div className={`shrink-0 text-center ${
                        isOverdue ? 'text-danger' :
                        isUrgent ? 'text-red-600' :
                        isWarning ? 'text-orange-600' : 'text-yellow-600'
                      }`}>
                        <p className="text-xl font-black leading-none">
                          {isOverdue ? `-${Math.abs(Math.round(r.sisa_hari))}` : Math.round(r.sisa_hari)}
                        </p>
                        <p className="text-[9px] font-bold uppercase">
                          {isOverdue ? 'Late' : 'Hari'}
                        </p>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-teal-main">{r.kode}</p>
                        <p className="text-sm font-semibold text-neutral-text-primary truncate">{r.judul}</p>
                        <p className="text-xs text-neutral-text-secondary">
                          PIC: {r.pic} • {targetEfektif ? new Date(targetEfektif).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                        </p>
                      </div>
                      
                      {/* Action */}
                      {role.canManageRapim && !isOverdue && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleSendReminder(r.laporan_id);
                          }}
                          className="p-2 rounded-lg bg-warning/10 hover:bg-warning/20 text-warning opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mb-3">
                <CheckCircle className="w-7 h-7 text-success" />
              </div>
              <p className="text-sm font-semibold text-neutral-text-primary">Tidak ada pengingat!</p>
              <p className="text-xs text-neutral-text-secondary mt-1">Semua deadline masih aman</p>
            </div>
          )}
        </Card>
      </div>

      {/* PPEPP Progress Tracking - Full Width Premium Card */}
      <Card padding="lg" className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-teal-main/5 to-transparent rounded-full -mr-32 -mt-32" />
        
        <div className="relative z-10">
          <SectionHeader
            icon={<TrendingUp className="w-5 h-5 text-teal-main" />}
            title="Progress Tindak Lanjut (PPEPP)"
            subtitle="Pantau status dan progress setiap tindak lanjut"
            action={
              <Link 
                to="/laporan?scope=pic" 
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-main/10 hover:bg-teal-main/20 text-teal-main text-xs font-semibold rounded-lg transition-colors"
              >
                Lihat Semua <ChevronRight className="w-3 h-3" />
              </Link>
            }
          />

          {/* Tab Navigation - Premium Style */}
          <div className="flex items-center gap-1 p-1 bg-neutral-bg/50 rounded-xl mb-6 overflow-x-auto">
            {PPEPP_TABS.map((tab) => {
              const count = ppeppStatusCounts[tab.id];
              const isActive = ppeppActiveTab === tab.id;
              const IconComponent = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => setPpeppActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap
                    ${isActive
                      ? 'bg-white text-teal-main shadow-sm'
                      : 'text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-white/50'
                    }
                  `}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span>{tab.label}</span>
                  <span className={`
                    min-w-[22px] h-[22px] flex items-center justify-center rounded-full text-[11px] font-bold
                    ${isActive
                      ? 'bg-teal-main/10 text-teal-main'
                      : count > 0
                        ? 'bg-neutral-border/50 text-neutral-text-primary'
                        : 'bg-neutral-border/30 text-neutral-text-secondary'
                    }
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* PPEPP List */}
          <div className="space-y-3">
            {paginatedPpeppList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-neutral-bg flex items-center justify-center mb-4">
                  <TrendingUp className="w-8 h-8 text-neutral-text-secondary opacity-40" />
                </div>
                <p className="text-sm font-semibold text-neutral-text-primary">
                  {ppeppActiveTab === 'semua'
                    ? 'Belum ada tindak lanjut.'
                    : `Tidak ada tindak lanjut dengan status "${PPEPP_TABS.find(t => t.id === ppeppActiveTab)?.label}".`
                  }
                </p>
              </div>
            ) : (
              paginatedPpeppList.map((item) => {
                const daysRemaining = item.target_date
                  ? Math.ceil((new Date(item.target_date).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24))
                  : null;
                const isOverdue = daysRemaining !== null && daysRemaining < 0;
                const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;
                const kondisi = (item as any).kondisi_selesai;
                const isSelesai = kondisi === 'selesai_final';
                const isTerhambat = kondisi === 'terhambat';
                const isMenunggu = kondisi === 'menunggu_konfirmasi';

                const cardBg = isSelesai 
                  ? 'bg-gradient-to-r from-success/5 to-transparent border-success/20 hover:border-success/40'
                  : isTerhambat
                    ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                    : isMenunggu
                      ? 'bg-gradient-to-r from-amber-50 to-transparent border-amber-200 hover:border-amber-300'
                      : isOverdue
                        ? 'bg-gradient-to-r from-danger/5 to-transparent border-danger/20 hover:border-danger/40'
                        : isUrgent
                          ? 'bg-gradient-to-r from-warning/5 to-transparent border-warning/20 hover:border-warning/40'
                          : 'bg-white border-neutral-border hover:border-teal-main/30';

                return (
                  <div
                    key={item.id}
                    className={`group p-5 rounded-2xl border transition-all duration-300 hover:shadow-lg ${cardBg}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                            {(item as any).laporan_masalah?.kode || (item as any).laporanMasalah?.kode}
                          </span>
                          
                          {/* Status Badge */}
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                            isSelesai ? 'bg-success/10 text-success' :
                            isTerhambat ? 'bg-danger/10 text-danger' :
                            isMenunggu ? 'bg-amber-100 text-amber-700' :
                            'bg-warning/10 text-warning'
                          }`}>
                            {isSelesai ? '✓ Selesai' :
                             isTerhambat ? 'Terhambat' :
                             isMenunggu ? 'Konfirmasi' :
                             'Berlangsung'}
                          </span>
                          
                          {/* Days Indicator */}
                          {daysRemaining !== null && !isSelesai && (
                            <span className={`text-[11px] font-semibold ${
                              isOverdue ? 'text-danger' : isUrgent ? 'text-warning' : 'text-neutral-text-secondary'
                            }`}>
                              {isOverdue ? `${Math.abs(daysRemaining)}d terlambat` : `${daysRemaining}d lagi`}
                            </span>
                          )}
                        </div>
                        
                        <h4 className="text-base font-bold text-neutral-text-primary truncate mb-1">
                          {(item as any).laporan_masalah?.judul || (item as any).laporanMasalah?.judul}
                        </h4>
                        
                        <div className="flex items-center gap-4 text-xs text-neutral-text-secondary">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {item.pic_user?.name || item.pic_unit?.name || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {item.target_date ? new Date(item.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}
                          </span>
                        </div>
                      </div>
                      
                      <Link to={`/tindak-lanjut/${item.id}`}>
                        <button className="p-2.5 rounded-xl bg-neutral-bg group-hover:bg-teal-main/10 text-neutral-text-secondary group-hover:text-teal-main transition-colors">
                          <Eye className="w-5 h-5" />
                        </button>
                      </Link>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <ProgressBar 
                          value={item.progress_percentage || 0} 
                          color={isSelesai ? 'success' : isTerhambat ? 'danger' : 'teal'} 
                          size="sm" 
                          showLabel={false}
                        />
                      </div>
                      <span className={`text-sm font-black min-w-[45px] text-right ${
                        isSelesai ? 'text-success' : 
                        isTerhambat ? 'text-danger' : 
                        'text-teal-main'
                      }`}>
                        {item.progress_percentage || 0}%
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination - Premium Style */}
          {ppeppTotalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-neutral-border">
              <p className="text-xs text-neutral-text-secondary">
                Menampilkan <span className="font-semibold text-neutral-text-primary">
                  {((ppeppCurrentPage - 1) * PPEPP_PER_PAGE) + 1} - {Math.min(ppeppCurrentPage * PPEPP_PER_PAGE, filteredPpeppList.length)}
                </span> dari <span className="font-semibold text-neutral-text-primary">{filteredPpeppList.length}</span> tindak lanjut
              </p>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPpeppCurrentPage(p => Math.max(1, p - 1))}
                  disabled={ppeppCurrentPage === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    ppeppCurrentPage === 1
                      ? 'text-neutral-text-secondary/30 cursor-not-allowed'
                      : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-teal-main'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-1 px-2">
                  {Array.from({ length: ppeppTotalPages }, (_, i) => i + 1)
                    .filter(page => {
                      if (page === 1 || page === ppeppTotalPages) return true;
                      if (Math.abs(page - ppeppCurrentPage) <= 1) return true;
                      return false;
                    })
                    .map((page, index, arr) => {
                      const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1;

                      return (
                        <React.Fragment key={page}>
                          {showEllipsisBefore && (
                            <span className="px-2 text-neutral-text-secondary">•••</span>
                          )}
                          <button
                            onClick={() => setPpeppCurrentPage(page)}
                            className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold transition-all ${
                              ppeppCurrentPage === page
                                ? 'bg-teal-main text-white shadow-sm'
                                : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-neutral-text-primary'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  onClick={() => setPpeppCurrentPage(p => Math.min(ppeppTotalPages, p + 1))}
                  disabled={ppeppCurrentPage === ppeppTotalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    ppeppCurrentPage === ppeppTotalPages
                      ? 'text-neutral-text-secondary/30 cursor-not-allowed'
                      : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-teal-main'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Riwayat Pengingat - Compact Table Style */}
      {role.canManageRapim && (
        <Card padding="lg">
          <SectionHeader
            icon={<Bell className="w-5 h-5 text-info" />}
            title="Riwayat Pengiriman Pengingat"
            subtitle="5 pengiriman terakhir"
          />
          
          {reminderHistory.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-neutral-border">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-bg/50">
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Waktu</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Laporan</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Penerima</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Kanal</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-border">
                  {reminderHistory.slice(0, 5).map((log) => (
                    <tr key={log.id} className="hover:bg-neutral-bg/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-xs text-neutral-text-secondary">
                          {log.dikirim_pada ? new Date(log.dikirim_pada).toLocaleString('id-ID', { 
                            day: '2-digit', 
                            month: 'short', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          }) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-teal-main">{log.laporan?.kode || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold text-neutral-text-primary">{log.penerima?.name || '-'}</p>
                          <p className="text-[10px] text-neutral-text-secondary">
                            {log.tipe_penerima === 'atasan_unit' ? 'Atasan Unit' : 
                             log.tipe_penerima === 'pic' ? 'PIC' : 'Pelapor'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          log.kanal === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {log.kanal === 'email' ? '📧 Email' : '🔔 Aplikasi'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                          log.status === 'berhasil' ? 'bg-success/10 text-success' :
                          log.status === 'gagal' ? 'bg-danger/10 text-danger' :
                          'bg-neutral-bg text-neutral-text-secondary'
                        }`}>
                          {log.status === 'berhasil' ? '✓' : log.status === 'gagal' ? '✕' : '○'} {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-14 h-14 rounded-full bg-neutral-bg flex items-center justify-center mb-3">
                <Bell className="w-7 h-7 text-neutral-text-secondary opacity-40" />
              </div>
              <p className="text-sm text-neutral-text-secondary">Belum ada riwayat pengiriman pengingat.</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default MonitoringDashboard;
